/* eslint-disable @typescript-eslint/no-explicit-any */

// =============================================================================
// TRANSIT GATEWAY TYPES
// =============================================================================

export interface TGWRoute {
  destination_cidr_block: string;
  type: 'static' | 'propagated';
  state: 'active' | 'blackhole' | 'pending' | 'deleting';
  attachment_id?: string;
  attachment_resource_type?: 'vpc' | 'vpn' | 'direct-connect-gateway' | 'peering' | 'connect';
  attachment_resource_id?: string;
}

export interface TGWRouteTable {
  id: string;
  tags?: { Name?: string; [key: string]: any };
  routes: TGWRoute[];
  default_association_route_table?: boolean;
  default_propagation_route_table?: boolean;
}

export interface TGWAttachmentAssociation {
  TransitGatewayRouteTableId: string;
  State?: string;
}

export interface TGWAttachment {
  id: string;
  resource_type: 'vpc' | 'vpn' | 'direct-connect-gateway' | 'peering' | 'connect';
  resource_id: string;
  state?: string;
  
  // VPC-specific
  vpc_name?: string;
  vpc_cidr?: string;
  subnet_ids?: string[];
  
  // VPN-specific
  vpn_connection_id?: string;
  
  // Direct Connect Gateway-specific
  direct_connect_gateway_id?: string;
  
  // Peering-specific
  peering_attachment_id?: string;
  peering_region?: string;
  
  // Association info
  association?: TGWAttachmentAssociation;
}

export interface TransitGateway {
  id: string;
  tags?: { Name?: string; [key: string]: any };
  state: 'available' | 'pending' | 'deleting' | 'deleted';
  owner_id?: string;
  amazon_side_asn?: number;
  default_route_table_id?: string;
  region?: string;
  route_tables: TGWRouteTable[];
  attachments: TGWAttachment[];
  is_owner?: boolean;
}

// =============================================================================
// REACT FLOW NODE DATA TYPES
// =============================================================================

export interface TGWNodeData {
  id: string;
  name: string;
  asn?: number;
  region?: string;
  state: string;
  routeTableCount: number;
  attachmentCount: number;
  isOwner: boolean;
  defaultRouteTableId?: string;
}

export interface TGWRouteTableNodeData {
  id: string;
  name: string;
  isDefault: boolean;
  routeCount: number;
  routes: TGWRoute[];
  associatedAttachments: string[];
  tgwId: string;
}

export interface TGWAttachmentNodeData {
  id: string;
  type: 'vpc' | 'vpn' | 'direct-connect-gateway' | 'peering' | 'connect';
  resourceId: string;
  resourceName?: string;
  state?: string;
  associatedRouteTableId?: string;
  // Type-specific data
  vpcCidr?: string;
  subnetIds?: string[];
  vpnConnectionId?: string;
  dxGatewayId?: string;
  peeringTgwId?: string;
  peeringRegion?: string;
  tgwId: string;
}

// =============================================================================
// STYLING CONSTANTS
// =============================================================================

export const ATTACHMENT_STYLES: Record<string, {
  bg: string;
  border: string;
  iconColor: string;
  label: string;
}> = {
  'vpc': {
    bg: 'bg-orange-50',
    border: 'border-orange-400',
    iconColor: 'text-orange-600',
    label: 'VPC'
  },
  'direct-connect-gateway': {
    bg: 'bg-purple-50',
    border: 'border-purple-400',
    iconColor: 'text-purple-600',
    label: 'DX Gateway'
  },
  'vpn': {
    bg: 'bg-green-50',
    border: 'border-green-400',
    iconColor: 'text-green-600',
    label: 'VPN'
  },
  'peering': {
    bg: 'bg-blue-50',
    border: 'border-blue-400',
    iconColor: 'text-blue-600',
    label: 'TGW Peering'
  },
  'connect': {
    bg: 'bg-cyan-50',
    border: 'border-cyan-400',
    iconColor: 'text-cyan-600',
    label: 'TGW Connect'
  }
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function getTagName(tags?: { Name?: string; [key: string]: any }): string | null {
  if (!tags) return null;
  return tags.Name || null;
}

export function formatAsn(asn?: number): string {
  if (!asn) return 'N/A';
  return asn.toLocaleString();
}

